import { memo, type FC } from 'react';
import { navigationHelper } from 'NavigationHelpers';
import { useSuspenseFeatureGate } from 'FrontRoyalStatsig';
import { VoiceAgentRoom } from 'TutorBotVoiceAgent';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import './locales/TutorBotCoach/CoachPage-en.json';
import { useCurrentUser } from 'FrontRoyalAngular';
import { UI_CONTEXT_COACH_AUDIO } from 'TutorBotConversation';
import { getProgramApplication } from 'Users';
import { CoachIntro } from '../CoachIntro/CoachIntro';

// The max width is set to be a reasonable reading width, since messages stretch all the way
// from one side to the other. We assume that the max reasonable reading width is our small
// breakpoint, 768px.
const MAX_WIDTH = 'max-w-[768px]';

export const CoachPagePresentation: FC<{ goHome: () => void; programApplicationId: string }> = ({
    goHome,
    programApplicationId,
}) => {
    // Using two different locale files here. Putting the more generic stuff into
    // TutorBotVoiceAgent where it could be re-used, and the stuff that is specific
    // to coaching here.
    const { t } = useTranslation('back_royal');

    // Because this page is rendered without the app shell, it needs to be absolute in order
    // to make it stretch the full height of the page.
    return (
        <div className={twMerge([MAX_WIDTH, 'mx-auto flex h-screen w-full items-center justify-center px-2.5'])}>
            <VoiceAgentRoom
                agentIdentifier="coach"
                extraTokenAttributes={{ programApplicationId }}
                uiContext={UI_CONTEXT_COACH_AUDIO}
                color="purple"
                IntroComponent={<CoachIntro />}
                startDiscussionText={t('TutorBotCoach.CoachPage.startCoachingSession')}
                abortText={t('TutorBotVoiceAgent.TutorBotVoiceAgent.doThisLater')}
                abortFromRoom={goHome}
                allowAbortDuringConversation={false}
            />
        </div>
    );
};

const CoachPageComponent: FC = () => {
    const currentUser = useCurrentUser();
    const programApplicationId = getProgramApplication(currentUser)?.id;
    const coachEnabled = useSuspenseFeatureGate('voice_coach');
    const { goHome } = navigationHelper();

    if (!coachEnabled || !programApplicationId) {
        goHome();
        return null;
    }

    return <CoachPagePresentation goHome={goHome} programApplicationId={programApplicationId} />;
};

export const CoachPage = memo(CoachPageComponent) as typeof CoachPageComponent;
